import { useState } from "react";
import { ArrowRight, Waypoints, X } from "lucide-react";
import { CompassRose, Rule, roman } from "./Ornament";
import { CooldownNotice } from "./CooldownNotice";
import { WaxSeal } from "./WaxSeal";
import { readSealCount } from "@/hooks/useSeals";
import { chipRoutes, CORE_COUNT, PROBE_COUNT } from "@/lib/catalog";

const ResumeCard = ({ route, onResume, onDismiss }) => {
  const total = route.steps?.length || 0;
  const done = readSealCount(route.id);
  return (
    <div data-testid="resume-route-card" className="plate-frame card-ink mt-8 max-w-lg mx-auto p-4 sm:p-5 flex items-center gap-4 text-left atlas-rise-3">
      <WaxSeal className="h-11 w-11 shrink-0" label={`${done} of ${total} sealed`} />
      <div className="min-w-0 flex-1">
        <span className="eyebrow text-muted-foreground">Resume your voyage{route.draft ? " · draft" : ""}</span>
        <p className="font-display text-lg leading-tight truncate">{route.destination}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          <span className="numeral text-primary">{roman(done)}</span> of <span className="numeral">{roman(total)}</span> steps sealed
        </p>
      </div>
      <button data-testid="resume-route-button" onClick={onResume} className="btn-brass px-4 py-2.5 shrink-0">
        Resume <ArrowRight className="h-4 w-4" />
      </button>
      <button data-testid="dismiss-resume-button" onClick={onDismiss} aria-label="Dismiss" className="grid place-items-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const STATUS = [
  { k: "Works now", v: `${CORE_COUNT} mapped topics, deterministic prerequisite routes, and a placement check on the ${PROBE_COUNT} instrumented steps in math, physics, chemistry & CS.`, tone: "text-primary" },
  { k: "Draft / generated", v: "AI-sketched routes for anything outside the catalog. Always labelled DRAFT, never the demo, no placement check.", tone: "text-muted-foreground" },
  { k: "Not built", v: "In-app lessons and the adaptive tutor. Each step links out to curated free sources instead.", tone: "text-muted-foreground" },
];

export const Hero = ({ onSubmit, onChip, cooldownUntil, onCooldownEnd, lastRoute, onResume, onDismissLast, onExploreMap }) => {
  const [value, setValue] = useState("");
  const cooling = !!cooldownUntil && cooldownUntil > Date.now();

  const submit = (e) => {
    e?.preventDefault();
    const v = value.trim();
    if (!v) return;
    onSubmit(v);
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 atlas-grid pointer-events-none" aria-hidden />
      <CompassRose className="atlas-spin-slow pointer-events-none absolute left-1/2 top-[-6rem] md:top-[-4rem] h-[30rem] w-[30rem] md:h-[40rem] md:w-[40rem] -translate-x-1/2 opacity-[0.16]" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-16 md:pt-24 pb-12 text-center">
        <Rule label="Where do you want to arrive?" className="max-w-md mx-auto mb-8 atlas-rise" />

        <h1 className="font-display font-medium tracking-tight leading-[1.0] text-4xl sm:text-5xl lg:text-[4.75rem] atlas-rise">
          Name a destination.
          <br />
          <span className="italic font-light text-brass-gradient">Atlas plans the route.</span>
        </h1>

        <dl data-testid="status-lines" className="mt-7 max-w-2xl mx-auto text-left grid gap-2 atlas-rise-2">
          {STATUS.map((s) => (
            <div key={s.k} className="grid grid-cols-[7.5rem_1fr] sm:grid-cols-[9rem_1fr] gap-3 items-baseline" data-testid={`status-${s.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <dt className={`eyebrow ${s.tone}`}>{s.k}</dt>
              <dd className="text-sm text-muted-foreground leading-relaxed">{s.v}</dd>
            </div>
          ))}
        </dl>

        <form onSubmit={submit} className="mt-9 atlas-rise-2">
          <div className="plate-frame relative flex items-center rounded-xl border border-border bg-card/80 backdrop-blur-md focus-within:border-primary/70 focus-within:shadow-[0_0_0_4px_hsl(var(--primary)/0.12),0_20px_50px_-20px_hsl(var(--brass-glow)/0.5)] transition-[border-color,box-shadow] duration-300 overflow-hidden">
            <input
              data-testid="hero-destination-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. Calculus, Quantum Mechanics, Machine Learning…"
              className="flex-1 min-w-0 bg-transparent pl-5 sm:pl-6 pr-2 py-5 text-base sm:text-lg font-display outline-none placeholder:text-muted-foreground/60 placeholder:italic"
            />
            <button data-testid="generate-route-button" type="submit" disabled={!value.trim()} className="btn-brass m-2 shrink-0 relative z-10">
              Plan route <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 atlas-rise-3">
          <span className="eyebrow text-muted-foreground mr-1">Catalog nodes</span>
          {chipRoutes().map((n) => (
            <button
              key={n.id}
              data-testid={`suggestion-${n.id.replace(/_/g, "-")}`}
              onClick={() => {
                setValue(n.name);
                onChip(n);
              }}
              className="rounded-full border border-border bg-card/50 px-3.5 py-1.5 text-sm font-display italic text-muted-foreground hover:text-primary hover:border-primary/60 transition-colors"
            >
              {n.name}
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-center atlas-rise-3">
          <button data-testid="explore-map-home-button" onClick={onExploreMap} className="group inline-flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <span className="grid place-items-center h-8 w-8 rounded-full border border-border bg-card/60 group-hover:border-primary/60 transition-colors">
              <Waypoints className="h-4 w-4 text-primary" strokeWidth={1.7} />
            </span>
            <span className="font-display italic">Or explore the whole map first</span>
          </button>
        </div>

        {cooling && <CooldownNotice until={cooldownUntil} onExpire={onCooldownEnd} />}
        {lastRoute && !cooling && <ResumeCard route={lastRoute} onResume={onResume} onDismiss={onDismissLast} />}
      </div>
    </section>
  );
};
